// Circular-mask crop modal; exports JPEG at native crop resolution (fresh Image load avoids iOS low-res drawImage).
// Fix: Safari was decoding from laid-out <img>, producing ~192px exports; now full bitmap + size from source crop.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

const VIEWPORT_PX = 300;
/** Max canvas edge (Safari / memory); export uses source crop size up to this cap. */
const MAX_EXPORT_SIDE_PX = 4096;
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;

function loadImageFullResolution(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = () => reject(new Error("image load failed"));
        im.src = src;
    });
}

type Props = {
    imageSrc: string;
    onClose: () => void;
    onApply: (blob: Blob) => void;
};

const PublicProfileImageCropModalComponent = ({ imageSrc, onClose, onApply }: Props) => {
    const imgRef = useRef<HTMLImageElement | null>(null);
    const [natural, setNatural] = useState({ w: 0, h: 0 });
    const [domImageReady, setDomImageReady] = useState(false);
    const [drag, setDrag] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const dragRef = useRef({
        active: false,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0,
        pointerId: -1,
    });
    const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
    const pinchRef = useRef({
        active: false,
        startDistance: 0,
        startZoom: 1,
        startCenterX: 0,
        startCenterY: 0,
        startDragX: 0,
        startDragY: 0,
    });

    const baseScale = useMemo(() => {
        if (!natural.w || !natural.h) return 1;
        return Math.max(VIEWPORT_PX / natural.w, VIEWPORT_PX / natural.h);
    }, [natural.w, natural.h]);

    const displayW = natural.w * baseScale * zoom;
    const displayH = natural.h * baseScale * zoom;

    const clampDrag = useCallback(
        (dx: number, dy: number, dW: number, dH: number) => {
            const s = VIEWPORT_PX;
            const minX = s / 2 - dW / 2;
            const maxX = dW / 2 - s / 2;
            const minY = s / 2 - dH / 2;
            const maxY = dH / 2 - s / 2;
            return {
                x: Math.min(maxX, Math.max(minX, dx)),
                y: Math.min(maxY, Math.max(minY, dy)),
            };
        },
        []
    );

    useEffect(() => {
        setDrag({ x: 0, y: 0 });
        setZoom(1);
        setNatural({ w: 0, h: 0 });
        setDomImageReady(false);
        let cancelled = false;
        const im = new Image();
        im.onload = () => {
            if (cancelled) return;
            setNatural({ w: im.naturalWidth, h: im.naturalHeight });
        };
        im.src = imageSrc;
        return () => {
            cancelled = true;
        };
    }, [imageSrc]);

    useEffect(() => {
        if (!natural.w) return;
        setDrag((d) => clampDrag(d.x, d.y, displayW, displayH));
    }, [zoom, displayW, displayH, clampDrag, natural.w]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        imgRef.current = e.currentTarget;
        setDomImageReady(true);
    };

    const onWheel = (ev: React.WheelEvent) => {
        if (!natural.w) return;
        ev.preventDefault();
        const factor = ev.deltaY > 0 ? 0.92 : 1.08;
        setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z * factor)));
    };

    const pointerDown = (ev: React.PointerEvent) => {
        if (!natural.w) return;
        (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
        pointersRef.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

        const pointers = Array.from(pointersRef.current.values());
        if (pointers.length >= 2) {
            const p1 = pointers[0];
            const p2 = pointers[1];
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            pinchRef.current = {
                active: true,
                startDistance: Math.max(1, Math.hypot(dx, dy)),
                startZoom: zoom,
                startCenterX: (p1.x + p2.x) / 2,
                startCenterY: (p1.y + p2.y) / 2,
                startDragX: drag.x,
                startDragY: drag.y,
            };
            dragRef.current.active = false;
            return;
        }

        dragRef.current = {
            active: true,
            startX: ev.clientX,
            startY: ev.clientY,
            originX: drag.x,
            originY: drag.y,
            pointerId: ev.pointerId,
        };
    };

    const pointerMove = (ev: React.PointerEvent) => {
        pointersRef.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

        if (pinchRef.current.active) {
            const pointers = Array.from(pointersRef.current.values());
            if (pointers.length >= 2) {
                const p1 = pointers[0];
                const p2 = pointers[1];
                const dx = p2.x - p1.x;
                const dy = p2.y - p1.y;
                const distance = Math.max(1, Math.hypot(dx, dy));
                const centerX = (p1.x + p2.x) / 2;
                const centerY = (p1.y + p2.y) / 2;
                const nextZoom = Math.min(
                    ZOOM_MAX,
                    Math.max(ZOOM_MIN, (pinchRef.current.startZoom * distance) / pinchRef.current.startDistance)
                );

                const panDx = centerX - pinchRef.current.startCenterX;
                const panDy = centerY - pinchRef.current.startCenterY;
                const nextDragX = pinchRef.current.startDragX + panDx;
                const nextDragY = pinchRef.current.startDragY + panDy;

                setZoom(nextZoom);
                setDrag(clampDrag(nextDragX, nextDragY, natural.w * baseScale * nextZoom, natural.h * baseScale * nextZoom));
                return;
            }
        }

        const dr = dragRef.current;
        if (!dr.active || ev.pointerId !== dr.pointerId) return;
        const dx = dr.originX + (ev.clientX - dr.startX);
        const dy = dr.originY + (ev.clientY - dr.startY);
        setDrag(clampDrag(dx, dy, displayW, displayH));
    };

    const pointerUp = (ev: React.PointerEvent) => {
        pointersRef.current.delete(ev.pointerId);
        const dr = dragRef.current;
        try {
            (ev.currentTarget as HTMLElement).releasePointerCapture(ev.pointerId);
        } catch {
            /* ignore */
        }
        if (ev.pointerId === dr.pointerId) {
            dragRef.current.active = false;
        }

        const pointers = Array.from(pointersRef.current.entries());
        if (pointers.length < 2) {
            pinchRef.current.active = false;
        }

        if (pointers.length === 1) {
            const [pointerId, point] = pointers[0];
            dragRef.current = {
                active: true,
                startX: point.x,
                startY: point.y,
                originX: drag.x,
                originY: drag.y,
                pointerId,
            };
        }
    };

    const exportSquareJpeg = async (): Promise<Blob | null> => {
        if (!natural.w || !domImageReady) return null;

        let bitmap: HTMLImageElement;
        try {
            bitmap = await loadImageFullResolution(imageSrc);
        } catch {
            return null;
        }

        const nw = bitmap.naturalWidth;
        const nh = bitmap.naturalHeight;
        const nwState = natural.w;
        const nhState = natural.h;
        if (!nw || !nh || !nwState || !nhState) return null;

        const S = VIEWPORT_PX;
        const dW = displayW;
        const dH = displayH;
        const leftPx = S / 2 - dW / 2 + drag.x;
        const topPx = S / 2 - dH / 2 + drag.y;

        const sx = (-leftPx / dW) * nwState;
        const sy = (-topPx / dH) * nhState;
        const sw = (S / dW) * nwState;
        const sh = (S / dH) * nhState;

        const xFactor = nw / nwState;
        const yFactor = nh / nhState;
        const cropSideSrc = Math.max(sw * xFactor, sh * yFactor);
        let exportSide = Math.max(1, Math.round(cropSideSrc));
        exportSide = Math.min(MAX_EXPORT_SIDE_PX, exportSide);

        const canvas = document.createElement("canvas");
        canvas.width = exportSide;
        canvas.height = exportSide;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, exportSide, exportSide);
        ctx.drawImage(
            bitmap,
            sx * xFactor,
            sy * yFactor,
            sw * xFactor,
            sh * yFactor,
            0,
            0,
            exportSide,
            exportSide
        );

        return new Promise((resolve) => {
            canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
        });
    };

    const onClickApply = async () => {
        const blob = await exportSquareJpeg();
        if (blob) onApply(blob);
    };

    const left = VIEWPORT_PX / 2 - displayW / 2 + drag.x;
    const top = VIEWPORT_PX / 2 - displayH / 2 + drag.y;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/45"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="relative z-[1] bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="profile-crop-title"
            >
                <div className="p-4 border-b border-gray-100 relative z-30 bg-white">
                    <h3 id="profile-crop-title" className="text-lg font-semibold text-gray-900">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "publicProfileEdit.txtCropTitle")}
                    </h3>
                </div>
                <div className="p-4 flex flex-col items-center relative z-0">
                    <div
                        className="relative touch-none select-none isolate"
                        style={{ width: VIEWPORT_PX, height: VIEWPORT_PX }}
                    >
                        <div
                            className="absolute rounded-full pointer-events-none z-20"
                            style={{
                                width: VIEWPORT_PX,
                                height: VIEWPORT_PX,
                                boxShadow: "0 0 0 100vmax rgba(0,0,0,0.55)",
                            }}
                        />
                        <div
                            className="absolute rounded-full pointer-events-none z-30 overflow-hidden"
                            style={{ width: VIEWPORT_PX, height: VIEWPORT_PX }}
                        >
                            <div className="absolute top-1/3 left-0 w-full h-px bg-black/45" />
                            <div className="absolute top-2/3 left-0 w-full h-px bg-black/45" />
                            <div className="absolute left-1/3 top-0 h-full w-px bg-black/45" />
                            <div className="absolute left-2/3 top-0 h-full w-px bg-black/45" />
                        </div>
                        <div
                            className="absolute inset-0 rounded-full overflow-hidden z-10 bg-white"
                            onWheel={onWheel}
                            onPointerDown={pointerDown}
                            onPointerMove={pointerMove}
                            onPointerUp={pointerUp}
                            onPointerCancel={pointerUp}
                            style={{ touchAction: "none" }}
                        >
                            {!natural.w && (
                                <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "publicProfileEdit.txtCropLoading")}
                                </div>
                            )}
                            <img
                                ref={imgRef}
                                src={imageSrc}
                                alt=""
                                draggable={false}
                                onLoad={onImageLoad}
                                className="absolute max-w-none max-h-none"
                                style={{
                                    width: natural.w ? displayW : 1,
                                    height: natural.h ? displayH : 1,
                                    left: natural.w ? left : 0,
                                    top: natural.h ? top : 0,
                                    objectFit: "fill",
                                    opacity: natural.w ? 1 : 0,
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex gap-3 p-4 border-t border-gray-100 bg-gray-50 relative z-30">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50"
                    >
                        {getLanguageValue(PAGE_TYPE.STUDENT, "publicProfileEdit.btnCropCancel")}
                    </button>
                    <button
                        type="button"
                        onClick={onClickApply}
                        disabled={!natural.w || !domImageReady}
                        className="flex-1 py-3 text-sm font-medium text-white bg-[#CF3688] rounded-xl hover:bg-[#B82F77] disabled:opacity-50"
                    >
                        {getLanguageValue(PAGE_TYPE.STUDENT, "publicProfileEdit.btnCropSave")}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PublicProfileImageCropModalComponent;
